/**
 * Parsed error object structure
 */
export type TParsedError = {
  status_code: number
  title: string | null
  detail: string | null
}

/**
 * Single error entry returned by the API.
 *
 * `code` and `meta` are optional and present only for endpoints that
 * opt in to structured errors (e.g. the analyses 422 lists one entry per
 * unmet data requirement, with `code: 'required_slice_not_activated'`).
 */
export type TApiErrorEntry = {
  status?: string
  title?: string
  detail?: string
  code?: string
  meta?: Record<string, unknown>
}

/**
 * Error response structure from API
 */
type TApiErrorResponse = {
  response?: {
    status?: number
    data?: {
      errors?: TApiErrorEntry[]
    }
  }
}

/**
 * Parse error response from API calls
 *
 * Extracts structured error details from API error responses.
 * Returns an object with status_code
 *
 * @param error - The error object caught from axios/fetch
 * @returns Parsed error object with status_code, title, and detail
 */
export const parseApiError = (error: TApiErrorResponse): TParsedError => {
  // Start with default values
  const parsedError: TParsedError = {
    status_code: error.response?.status || 500,
    title: null,
    detail: null
  }

  // Try to extract structured error details from response
  if (
    error.response?.data?.errors &&
    Array.isArray(error.response.data.errors)
  ) {
    const firstError = error.response.data.errors[0]

    if (
      firstError &&
      firstError.status &&
      firstError.title &&
      firstError.detail
    ) {
      parsedError.status_code = parseInt(firstError.status, 10) || 500
      parsedError.title = firstError.title
      parsedError.detail = firstError.detail
    }
  }

  return parsedError
}

/**
 * Return every structured error entry from an API error response.
 *
 * Use this when callers need access to `code` / `meta` (not just the
 * human-readable `detail`) — e.g. the analyses 422 returns one entry per
 * unmet data requirement, with a `code` and the identifiers in `meta`.
 * The frontend reads `meta` to render the user-visible Swedish message.
 *
 * @param error - The error object caught from axios/fetch
 * @returns Array of error entries (empty if none found)
 */
export const parseApiErrors = (error: TApiErrorResponse): TApiErrorEntry[] => {
  const errors = error.response?.data?.errors

  if (!errors || !Array.isArray(errors)) {
    return []
  }

  return errors
}
